import React, { useEffect, useRef } from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";

import logo from "./zpa.png";
import schoolQRCode from "./zpaqrcode.png";
import api from "../../api/api";

/* ==========================================================================
   ZION PRAISE ACADEMY CRM SCHOOL SYSTEM
   PROFESSIONAL STUDENT PERIODIC REPORT

   A4 LANDSCAPE • TWO STUDENTS PER PAGE

   FEATURES:
   - Larger, readable text throughout the report.
   - Vertically centered subject names and score circles.
   - Professional vertical fraction class ranking.
   - Extra space between circles and horizontal table lines.
   - Responsive subject font and circle sizing.
   - Student admission photos and school QR code.
   - Performance summary, signatures and report footer.
   - Existing report data structure preserved.
   - Automatic PDF download and callbacks preserved.
   ========================================================================== */

const SCHOOL_NAME = "Zion Praise Academy CRM School System";

const SCHOOL_SUBTITLE =
  "Elementary, Junior & Senior High School";

const SCHOOL_ADDRESS =
  "Cowfield 1st Junction Duportroad Paynesville, Monrovia, Liberia";

const SCHOOL_CONTACT =
  "Contact: +231-775-912-321 / +231-881-403-326 / +231-777-506-311";

const SYSTEM_NAME = "Polu Integrated School Management System";

/* ==========================================================================
   COLOR PALETTE
   ========================================================================== */

const COLORS = {
  royalBlue: "#000000",
  royalDeep: "#0A2B61",
  royalMid: "#CD9E04",
  skyBlue: "#65B9F2",
  skyLight: "#EAF6FF",
  paleBlue: "#F4F9FE",
  softBlue: "#E0EFFB",
  white: "#FFFFFF",
  page: "#FFFFFF",
  text: "#202A36",
  textSecondary: "#4B5563",
  muted: "#6B7280",
  border: "#D9E4EF",
  borderDark: "#B8CADC",
  blue: "#013CAC",
  greenLight: "#EDF8F1",
  red: "#B42332",
  redLight: "#FCEFF0",
  grayLight: "#F7F9FC",
  gray: "#E9EEF4",
};

/* ==========================================================================
   PAGE CONFIGURATION
   ========================================================================== */

const API_BASE = api?.defaults?.baseURL || "";

const PAGE_PADDING_X = 14;
const PAGE_PADDING_TOP = 9;
const PAGE_PADDING_BOTTOM = 25;

const CARD_HEIGHT = 548;

/* ==========================================================================
   PROFESSIONAL PDF STYLES
   ========================================================================== */

const styles = StyleSheet.create({
  /* PAGE */

  page: {
    paddingTop: PAGE_PADDING_TOP,
    paddingBottom: PAGE_PADDING_BOTTOM,
    paddingHorizontal: PAGE_PADDING_X,
    backgroundColor: COLORS.page,
    fontFamily: "Helvetica",
    color: COLORS.text,
  },

  reportWrapper: {
    width: "100%",
    height: CARD_HEIGHT,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "stretch",
  },

  /* STUDENT CARD */

  card: {
    width: "49.45%",
    height: CARD_HEIGHT,
    paddingTop: 9,
    paddingBottom: 6,
    paddingHorizontal: 9,
    backgroundColor: COLORS.white,
    borderWidth: 0.8,
    borderColor: COLORS.borderDark,
    borderRadius: 5,
    position: "relative",
    overflow: "hidden",
  },

  emptyCard: {
    width: "49.45%",
    height: CARD_HEIGHT,
  },

  cardTopAccent: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    backgroundColor: COLORS.royalDeep,
  },

  cardSkyAccent: {
    position: "absolute",
    top: 3,
    left: 0,
    width: "32%",
    height: 2,
    backgroundColor: COLORS.skyBlue,
  },

  /* SCHOOL HEADER */

  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: 2,
    paddingBottom: 5,
    marginBottom: 4,
    borderBottomWidth: 0.8,
    borderBottomColor: COLORS.border,
  },

  logoFrame: {
    width: 43,
    height: 43,
    flexShrink: 0,
    marginRight: 8,
    padding: 2,
    borderWidth: 0.8,
    borderColor: COLORS.softBlue,
    borderRadius: 5,
    backgroundColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
  },

  logo: {
    width: 37,
    height: 37,
    objectFit: "contain",
  },

  schoolInfo: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    paddingRight: 5,
  },

  schoolName: {
    fontSize: 12,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    marginBottom: 2,
  },

  schoolSubtitle: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.royalMid,
    marginBottom: 2,
  },

  schoolAddress: {
    fontSize: 6.2,
    lineHeight: 1.25,
    color: COLORS.textSecondary,
    marginBottom: 1,
  },

  schoolReportBadge: {
    alignSelf: "flex-start",
    backgroundColor: COLORS.skyLight,
    borderWidth: 0.6,
    borderColor: COLORS.softBlue,
    borderRadius: 3,
    paddingVertical: 3,
    paddingHorizontal: 6,
    marginTop: 3,
  },

  reportTitle: {
    fontSize: 7,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    letterSpacing: 0.4,
  },

  /* STUDENT PHOTO */

  admissionPhotoColumn: {
    width: 48,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },

  admissionPhotoFrame: {
    width: 42,
    height: 47,
    padding: 2,
    borderWidth: 0.8,
    borderColor: COLORS.skyBlue,
    borderRadius: 4,
    backgroundColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },

  admissionPhoto: {
    width: 36,
    height: 41,
    objectFit: "cover",
    borderRadius: 2,
  },

  photoPlaceholder: {
    width: 36,
    height: 41,
    backgroundColor: COLORS.skyLight,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 2,
  },

  photoPlaceholderText: {
    fontSize: 5.5,
    fontWeight: "bold",
    textAlign: "center",
    color: COLORS.royalDeep,
  },

  photoCaption: {
    marginTop: 2,
    fontSize: 5.5,
    color: COLORS.muted,
    textAlign: "center",
  },

  /* SECTION HEADINGS */

  sectionHeading: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 3,
    marginBottom: 4,
  },

  sectionAccent: {
    width: 3,
    height: 10,
    backgroundColor: COLORS.skyBlue,
    borderRadius: 2,
    marginRight: 5,
  },

  sectionHeadingText: {
    fontSize: 8,
    color: COLORS.royalDeep,
    fontWeight: "bold",
    letterSpacing: 0.4,
  },

  /* STUDENT INFORMATION */

  infoBox: {
    flexDirection: "row",
    alignItems: "stretch",
    backgroundColor: COLORS.paleBlue,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderRadius: 4,
    paddingVertical: 5,
    paddingHorizontal: 6,
    marginBottom: 4,
  },

  studentDetails: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingRight: 5,
    justifyContent: "center",
  },

  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 3,
  },

  infoRowLast: {
    marginBottom: 0,
  },

  label: {
    width: 75,
    flexShrink: 0,
    fontSize: 7,
    color: COLORS.textSecondary,
    fontWeight: "bold",
  },

  value: {
    flexGrow: 1,
    flexShrink: 1,
    fontSize: 7.5,
    color: COLORS.text,
    fontWeight: "bold",
  },

  studentNameValue: {
    color: COLORS.royalDeep,
    fontSize: 8.5,
  },

  /* QR CODE */

  qrColumn: {
    width: 48,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    borderLeftWidth: 0.7,
    borderLeftColor: COLORS.border,
    paddingLeft: 5,
  },

  qrFrame: {
    width: 35,
    height: 35,
    padding: 2,
    borderWidth: 0.7,
    borderColor: COLORS.softBlue,
    borderRadius: 3,
    backgroundColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
  },

  qrImage: {
    width: 29,
    height: 29,
    objectFit: "contain",
  },

  qrCaption: {
    marginTop: 2,
    fontSize: 5,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    textAlign: "center",
  },

  /* SUBJECT TABLE */

  table: {
    width: "100%",
    borderWidth: 0.8,
    borderColor: COLORS.borderDark,
    borderRadius: 3,
    overflow: "hidden",
    marginTop: 1,
  },

  tableHeader: {
    flexDirection: "row",
    alignItems: "stretch",
    backgroundColor: COLORS.royalDeep,
    borderBottomWidth: 1.3,
    borderBottomColor: COLORS.skyBlue,
  },

  headerSubject: {
    width: "74%",
    paddingVertical: 5,
    paddingLeft: 7,
    paddingRight: 4,
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.white,
    letterSpacing: 0.4,
    borderRightWidth: 0.8,
    borderRightColor: COLORS.borderDark,
  },

  headerScore: {
    width: "26%",
    paddingVertical: 5,
    paddingHorizontal: 3,
    textAlign: "center",
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.white,
    letterSpacing: 0.35,
  },

  tableRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 0.55,
    borderBottomColor: COLORS.borderDark,
  },

  tableRowAlternate: {
    backgroundColor: COLORS.paleBlue,
  },

  /* SUBJECT CELL */

  subjectCell: {
    width: "74%",
    height: "100%",
    paddingLeft: 7,
    paddingRight: 5,
    paddingVertical: 1.5,
    borderRightWidth: 0.7,
    borderRightColor: COLORS.borderDark,
    justifyContent: "center",
    alignItems: "flex-start",
  },

  subjectText: {
    width: "100%",
    fontSize: 8,
    color: COLORS.text,
    textAlign: "left",
    lineHeight: 1.15,
  },

  /* SCORE CELL */

  scoreCell: {
    width: "26%",
    height: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 3,
    paddingVertical: 1.5,
  },

  scorePill: {
    width: 16,
    height: 16,
    flexShrink: 0,
    borderWidth: 0.7,
    borderColor: COLORS.borderDark,
    borderRadius: 8,
    backgroundColor: COLORS.white,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
  },

  scorePillGood: {
    borderColor: "#B7DCC5",
    backgroundColor: COLORS.greenLight,
  },

  scorePillBad: {
    borderColor: "#E8B9BE",
    backgroundColor: COLORS.redLight,
  },

  scorePillNeutral: {
    borderColor: COLORS.softBlue,
    backgroundColor: COLORS.white,
  },

  scoreText: {
    fontSize: 7.2,
    fontWeight: "bold",
    textAlign: "center",
    color: COLORS.royalDeep,
  },

  scoreGood: {
    color: COLORS.blue,
  },

  scoreBad: {
    color: COLORS.red,
  },

  scoreNeutral: {
    color: COLORS.royalDeep,
  },

  emptySubjects: {
    width: "100%",
    textAlign: "center",
    fontSize: 7.5,
    paddingVertical: 8,
    color: COLORS.textSecondary,
  },

  /* PERFORMANCE SUMMARY */

  summaryBox: {
    marginTop: 5,
    paddingVertical: 5,
    paddingHorizontal: 7,
    backgroundColor: COLORS.paleBlue,
    borderWidth: 0.7,
    borderColor: COLORS.border,
    borderLeftWidth: 2,
    borderLeftColor: COLORS.royalDeep,
    borderRadius: 3,
  },

  summaryHeading: {
    fontSize: 7.5,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    marginBottom: 4,
    letterSpacing: 0.3,
  },

  summaryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 3,
  },

  summaryRowLast: {
    marginBottom: 0,
  },

  summaryLabel: {
    flex: 1,
    fontSize: 7,
    color: COLORS.textSecondary,
    fontWeight: "bold",
  },

  summaryValue: {
    flex: 1,
    fontSize: 7.5,
    color: COLORS.text,
    fontWeight: "bold",
    textAlign: "right",
  },

  averagePill: {
    backgroundColor: COLORS.white,
    borderWidth: 0.7,
    borderColor: COLORS.softBlue,
    borderRadius: 12,
    paddingVertical: 2,
    paddingHorizontal: 6,
    alignSelf: "flex-end",
  },

  averageValue: {
    fontSize: 8,
    color: COLORS.royalDeep,
    fontWeight: "bold",
  },

  remarkValue: {
    color: COLORS.blue,
  },

  /* PROFESSIONAL VERTICAL FRACTION RANK */

  rankFraction: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 30,
    alignSelf: "flex-end",
  },

  rankNumerator: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    textAlign: "center",
    lineHeight: 1.1,
  },

  rankDivider: {
    width: 24,
    height: 0.9,
    backgroundColor: COLORS.royalMid,
    marginVertical: 1.5,
  },

  rankDenominator: {
    fontSize: 8,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    textAlign: "center",
    lineHeight: 1.1,
  },

  /* SIGNATURES */

  signatures: {
    marginTop: 7,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  signatureBox: {
    width: "43%",
    alignItems: "center",
  },

  signatureLine: {
    width: "100%",
    borderTopWidth: 0.8,
    borderTopColor: COLORS.borderDark,
    marginBottom: 3,
  },

  signatureText: {
    fontSize: 7.2,
    fontWeight: "bold",
    color: COLORS.royalDeep,
    textAlign: "center",
  },

  signatureSubtext: {
    fontSize: 6.2,
    color: COLORS.muted,
    textAlign: "center",
    marginTop: 1,
  },

  /* CARD FOOTER */

  cardFooter: {
    marginTop: 5,
    paddingTop: 3,
    borderTopWidth: 0.6,
    borderTopColor: COLORS.border,
    alignItems: "center",
  },

  cardFooterText: {
    fontSize: 5.3,
    color: COLORS.muted,
    textAlign: "center",
  },

  /* PAGE FOOTER */

  pageFooter: {
    position: "absolute",
    left: PAGE_PADDING_X,
    right: PAGE_PADDING_X,
    bottom: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTopWidth: 0.8,
    borderTopColor: COLORS.border,
    paddingTop: 3,
  },

  pageFooterLeft: {
    fontSize: 6,
    color: COLORS.textSecondary,
  },

  pageFooterRight: {
    fontSize: 6.5,
    color: COLORS.royalDeep,
    fontWeight: "bold",
  },
});

/* ==========================================================================
   SCORE HELPERS
   ========================================================================== */

const isLowScore = (score) => {
  if (typeof score === "number") {
    return score < 70;
  }

  if (typeof score === "string") {
    const normalized = score.trim().toUpperCase();

    if (["D", "F"].includes(normalized)) {
      return true;
    }

    const numericScore = Number(normalized);

    if (normalized !== "" && Number.isFinite(numericScore)) {
      return numericScore < 70;
    }
  }

  return false;
};

const getScoreStyle = (score) => {
  if (score === null || score === undefined || score === "") {
    return styles.scoreNeutral;
  }

  return isLowScore(score)
    ? styles.scoreBad
    : styles.scoreGood;
};

const getScorePillStyle = (score) => {
  if (score === null || score === undefined || score === "") {
    return styles.scorePillNeutral;
  }

  return isLowScore(score)
    ? styles.scorePillBad
    : styles.scorePillGood;
};

const groupReports = (reports = []) => {
  const pages = [];

  for (let i = 0; i < reports.length; i += 2) {
    pages.push(reports.slice(i, i + 2));
  }

  return pages;
};

const displayValue = (value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return String(value);
};

/* ==========================================================================
   RANK HELPERS
   ========================================================================== */

const getRankParts = (rank) => {
  const value = displayValue(rank);

  const parts = value.split("/");

  if (
    parts.length !== 2 ||
    !parts.every(
      (part) =>
        part.trim() !== "" &&
        Number.isFinite(Number(part))
    )
  ) {
    return null;
  }

  return {
    numerator: parts[0].trim(),
    denominator: parts[1].trim(),
  };
};

/* ==========================================================================
   ADMISSION PHOTO URL HELPERS
   ========================================================================== */

const getImageString = (value) => {
  if (typeof value === "string") {
    const result = value.trim();
    return result || null;
  }

  if (value && typeof value === "object") {
    if (typeof value.url === "string" && value.url.trim()) {
      return value.url.trim();
    }

    if (typeof value.uri === "string" && value.uri.trim()) {
      return value.uri.trim();
    }
  }

  return null;
};

const makeAbsoluteMediaUrl = (imageUrl) => {
  if (!imageUrl || typeof imageUrl !== "string") {
    return null;
  }

  const value = imageUrl.trim();

  if (!value) {
    return null;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (/^data:image\//i.test(value)) {
    return value;
  }

  if (value.startsWith("//")) {
    if (typeof window !== "undefined" && window.location?.protocol) {
      return `${window.location.protocol}${value}`;
    }

    return `https:${value}`;
  }

  if (API_BASE) {
    try {
      const apiUrl =
        typeof window !== "undefined"
          ? new URL(API_BASE, window.location.origin)
          : new URL(API_BASE);

      if (value.startsWith("/")) {
        return `${apiUrl.origin}${value}`;
      }

      return `${apiUrl.origin}/${value}`;
    } catch (error) {
      console.warn(
        "Could not resolve API base URL for student photo."
      );
    }
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    try {
      return new URL(value, window.location.origin).href;
    } catch (error) {
      console.warn(
        "Could not resolve student admission photo URL."
      );
    }
  }

  return value;
};

const getAdmissionImage = (report) => {
  const candidates = [
    report?.student_admission_image_url,
    report?.student_admission_image,
    report?.admission_image_url,
    report?.admission_image,

    report?.student?.student_admission_image_url,
    report?.student?.student_admission_image,
    report?.student?.admission_image_url,
    report?.student?.admission_image,

    report?.student_admission?.student_admission_image_url,
    report?.student_admission?.student_admission_image,
    report?.student_admission?.admission_image_url,
    report?.student_admission?.admission_image,

    report?.admission?.student_admission_image_url,
    report?.admission?.student_admission_image,
    report?.admission?.admission_image_url,
    report?.admission?.admission_image,

    report?.student?.admission?.student_admission_image_url,
    report?.student?.admission?.student_admission_image,
  ];

  for (const candidate of candidates) {
    const image = getImageString(candidate);

    if (image) {
      return makeAbsoluteMediaUrl(image);
    }
  }

  return null;
};

/* ==========================================================================
   STUDENT PERIODIC REPORT CARD
   ========================================================================== */

const StudentGradeCard = ({ report }) => {
  if (!report) {
    return <View style={styles.emptyCard} />;
  }

  const subjects = Array.isArray(report?.student_subjects)
    ? report.student_subjects
    : [];

  const subjectCount = subjects.length;

  /*
   * Keep rows taller than their score circles.
   * A modest minimum row height protects text and circle spacing.
   */

  const subjectRowHeight =
    subjectCount === 0
      ? 14
      : subjectCount > 32
        ? 12
        : subjectCount > 24
          ? 14
          : subjectCount > 16
            ? 16
            : 19;

  const subjectFontSize =
    subjectCount > 32
      ? 5.8
      : subjectCount > 24
        ? 6.4
        : subjectCount > 16
          ? 7
          : 8;

  const scoreFontSize =
    subjectCount > 32
      ? 4.8
      : subjectCount > 24
        ? 5.4
        : subjectCount > 16
          ? 6
          : 7.2;

  const scoreCircleSize =
    subjectCount > 32
      ? 9
      : subjectCount > 24
        ? 11
        : subjectCount > 16
          ? 13
          : 16;

  const admissionImage = getAdmissionImage(report);

  const studentName =
    report.student_name ||
    report.full_name ||
    report.student?.full_name ||
    report.student?.student_name;

  const gradeClassName =
    report.grade_class_name ||
    report.grade_class?.name;

  const periodName =
    report.period_name ||
    report.period?.name;

  const academicYearName =
    report.academic_year?.name ||
    report.academic_year_name ||
    (typeof report.academic_year === "string"
      ? report.academic_year
      : undefined);

  const rankParts = getRankParts(report.rank);

  return (
    <View style={styles.card} wrap={false}>
      <View style={styles.cardTopAccent} />
      <View style={styles.cardSkyAccent} />

      {/* SCHOOL HEADER */}

      <View style={styles.header}>
        <View style={styles.logoFrame}>
          <Image src={logo} style={styles.logo} />
        </View>

        <View style={styles.schoolInfo}>
          <Text style={styles.schoolName}>
            {SCHOOL_NAME}
          </Text>

          <Text style={styles.schoolSubtitle}>
            {SCHOOL_SUBTITLE}
          </Text>

          <Text style={styles.schoolAddress}>
            {SCHOOL_ADDRESS}
          </Text>

          <Text style={styles.schoolAddress}>
            {SCHOOL_CONTACT}
          </Text>

          <View style={styles.schoolReportBadge}>
            <Text style={styles.reportTitle}>
              STUDENT PERIODIC REPORT
            </Text>
          </View>
        </View>

        <View style={styles.admissionPhotoColumn}>
          <View style={styles.admissionPhotoFrame}>
            {admissionImage ? (
              <Image
                src={admissionImage}
                style={styles.admissionPhoto}
              />
            ) : (
              <View style={styles.photoPlaceholder}>
                <Text style={styles.photoPlaceholderText}>
                  {"STUDENT\nPHOTO"}
                </Text>
              </View>
            )}
          </View>

          <Text style={styles.photoCaption}>
            STUDENT PHOTO
          </Text>
        </View>
      </View>

      {/* STUDENT INFORMATION */}

      <View style={styles.sectionHeading}>
        <View style={styles.sectionAccent} />
        <Text style={styles.sectionHeadingText}>
          STUDENT INFORMATION
        </Text>
      </View>

      <View style={styles.infoBox}>
        <View style={styles.studentDetails}>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Student Name</Text>

            <Text
              style={[styles.value, styles.studentNameValue]}
              maxLines={1}
            >
              {displayValue(studentName)}
            </Text>
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.label}>Grade / Class</Text>

            <Text style={styles.value} maxLines={1}>
              {displayValue(gradeClassName)}
            </Text>
          </View>

          <View style={styles.infoRow}>
            <Text style={styles.label}>Academic Period</Text>

            <Text style={styles.value} maxLines={1}>
              {displayValue(periodName)}
            </Text>
          </View>

          <View style={[styles.infoRow, styles.infoRowLast]}>
            <Text style={styles.label}>Academic Year</Text>

            <Text style={styles.value} maxLines={1}>
              {displayValue(academicYearName)}
            </Text>
          </View>
        </View>

        <View style={styles.qrColumn}>
          <View style={styles.qrFrame}>
            <Image src={schoolQRCode} style={styles.qrImage} />
          </View>

          <Text style={styles.qrCaption}>
            SCHOOL QR CODE
          </Text>
        </View>
      </View>

      {/* ACADEMIC PERFORMANCE */}

      <View style={styles.sectionHeading}>
        <View style={styles.sectionAccent} />

        <Text style={styles.sectionHeadingText}>
          ACADEMIC PERFORMANCE
        </Text>
      </View>

      {/* SUBJECT AND SCORE TABLE */}

      <View style={styles.table} wrap={false}>
        <View style={styles.tableHeader} wrap={false}>
          <Text style={styles.headerSubject}>
            SUBJECTS
          </Text>

          <Text style={styles.headerScore}>
            SCORE
          </Text>
        </View>

        {subjects.length > 0 ? (
          subjects.map((subject, index) => {
            const score = subject.formatted_final_score;

            return (
              <View
                key={`${subject.subject_name || "subject"}-${index}`}
                style={[
                  styles.tableRow,
                  index % 2 === 1 && styles.tableRowAlternate,
                  {
                    height: subjectRowHeight,
                  },
                ]}
                wrap={false}
              >
                {/* CENTERED SUBJECT NAME */}

                <View style={styles.subjectCell}>
                  <Text
                    style={[
                      styles.subjectText,
                      {
                        fontSize: subjectFontSize,
                      },
                    ]}
                    maxLines={1}
                  >
                    {displayValue(
                      subject.subject_name,
                      "Unnamed Subject"
                    )}
                  </Text>
                </View>

                {/* CENTERED SCORE CIRCLE */}

                <View style={styles.scoreCell}>
                  <View
                    style={[
                      styles.scorePill,
                      getScorePillStyle(score),
                      {
                        width: scoreCircleSize,
                        height: scoreCircleSize,
                        borderRadius: scoreCircleSize / 2,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.scoreText,
                        getScoreStyle(score),
                        {
                          fontSize: scoreFontSize,
                        },
                      ]}
                      maxLines={1}
                    >
                      {displayValue(score)}
                    </Text>
                  </View>
                </View>
              </View>
            );
          })
        ) : (
          <View style={styles.tableRow} wrap={false}>
            <Text style={styles.emptySubjects}>
              No subject records are available for this report.
            </Text>
          </View>
        )}
      </View>

      {/* PERFORMANCE SUMMARY */}

      <View style={styles.summaryBox} wrap={false}>
        <Text style={styles.summaryHeading}>
          PERFORMANCE SUMMARY
        </Text>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>
            Average Score
          </Text>

          <View style={styles.averagePill}>
            <Text
              style={[
                styles.averageValue,
                getScoreStyle(report.average_score),
              ]}
            >
              {displayValue(report.average_score)}
            </Text>
          </View>
        </View>

        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>
            Teacher's Remark
          </Text>

          <Text
            style={[styles.summaryValue, styles.remarkValue]}
            maxLines={2}
          >
            {displayValue(report.remark)}
          </Text>
        </View>

        {/* CLASS RANK - PROFESSIONAL VERTICAL FRACTION */}

        <View style={[styles.summaryRow, styles.summaryRowLast]}>
          <Text style={styles.summaryLabel}>
            Class Rank
          </Text>

          {rankParts ? (
            <View style={styles.rankFraction}>
              <Text style={styles.rankNumerator}>
                {rankParts.numerator}
              </Text>

              <View style={styles.rankDivider} />

              <Text style={styles.rankDenominator}>
                {rankParts.denominator}
              </Text>
            </View>
          ) : (
            <Text style={styles.summaryValue}>
              {displayValue(report.rank)}
            </Text>
          )}
        </View>
      </View>

      {/* SIGNATURES */}

      <View style={styles.signatures} wrap={false}>
        <View style={styles.signatureBox}>
          <View style={styles.signatureLine} />

          <Text style={styles.signatureText}>
            Class Teacher
          </Text>

          <Text style={styles.signatureSubtext}>
            Signature / Date
          </Text>
        </View>

        <View style={styles.signatureBox}>
          <View style={styles.signatureLine} />

          <Text style={styles.signatureText}>
            Principal
          </Text>

          <Text style={styles.signatureSubtext}>
            Signature / Date
          </Text>
        </View>
      </View>

      {/* CARD FOOTER */}

      <View style={styles.cardFooter}>
        <Text style={styles.cardFooterText}>
          {SCHOOL_NAME} • Generated by {SYSTEM_NAME}
        </Text>
      </View>
    </View>
  );
};

/* ==========================================================================
   CLASS REPORT DOCUMENT
   TWO STUDENTS PER A4 LANDSCAPE PAGE
   ========================================================================== */

export const ClassReportDocument = ({
  reports = [],
  gradeClass,
  period,
}) => {
  const pages = groupReports(reports);

  return (
    <Document
      title={`${SCHOOL_NAME} - ${gradeClass?.name || ""} ${
        period?.name || ""
      } Report`}
      author={SCHOOL_NAME}
      subject="Student Periodic Grade Report"
      creator={SYSTEM_NAME}
      producer="React PDF"
    >
      {pages.map((pageReports, pageIndex) => (
        <Page
          key={pageIndex}
          size="A4"
          orientation="landscape"
          style={styles.page}
          wrap={false}
        >
          <View style={styles.reportWrapper} wrap={false}>
            <StudentGradeCard report={pageReports[0]} />

            {pageReports[1] ? (
              <StudentGradeCard report={pageReports[1]} />
            ) : (
              <View style={styles.emptyCard} />
            )}
          </View>

          <View fixed style={styles.pageFooter}>
            <Text style={styles.pageFooterLeft}>
              {SCHOOL_NAME} • {SYSTEM_NAME}
            </Text>

            <Text
              style={styles.pageFooterRight}
              render={({ pageNumber, totalPages }) =>
                `PAGE ${pageNumber} OF ${totalPages}`
              }
            />
          </View>
        </Page>
      ))}
    </Document>
  );
};

/* ==========================================================================
   AUTOMATIC PDF DOWNLOAD
   ========================================================================== */

const ClassReportPDF = ({
  reports,
  gradeClass,
  period,
  onDownloaded,
  onError,
}) => {
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (!reports?.length) {
      hasStartedRef.current = false;
      return undefined;
    }

    if (hasStartedRef.current) {
      return undefined;
    }

    hasStartedRef.current = true;

    let isCancelled = false;
    let downloadUrl = null;

    const generateAndDownload = async () => {
      try {
        const blob = await pdf(
          <ClassReportDocument
            reports={reports}
            gradeClass={gradeClass}
            period={period}
          />
        ).toBlob();

        if (isCancelled) {
          return;
        }

        downloadUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = downloadUrl;

        link.download = [
          "Zion_Praise_Academy",
          gradeClass?.name || "Class",
          period?.name || "Report",
        ]
          .join("_")
          .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-") + ".pdf";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        onDownloaded?.();
      } catch (error) {
        if (!isCancelled) {
          console.error("PDF generation failed:", error);
          onError?.(error);
        }
      } finally {
        if (downloadUrl) {
          URL.revokeObjectURL(downloadUrl);
        }
      }
    };

    generateAndDownload();

    return () => {
      isCancelled = true;
    };
  }, [
    reports,
    gradeClass,
    period,
    onDownloaded,
    onError,
  ]);

  return null;
};

export default ClassReportPDF;




// import React, { useEffect, useRef } from "react";
// import {
//   Document,
//   Page,
//   Text,
//   View,
//   Image,
//   StyleSheet,
//   pdf,
// } from "@react-pdf/renderer";

// import logo from "./zpa.png";
// import schoolQRCode from "./zpaqrcode.png";
// import api from "../../api/api";

// /* ==========================================================================
//    ZION PRAISE ACADEMY CRM SCHOOL SYSTEM
//    PROFESSIONAL STUDENT PERIODIC REPORT

//    A4 LANDSCAPE • TWO STUDENTS PER PAGE

//    FEATURES:
//    - Larger, more readable text throughout the report.
//    - Vertically centered subject names and score circles.
//    - Extra space between circles and horizontal table lines.
//    - Responsive subject font and circle sizing for longer lists.
//    - Student admission photos and school QR code.
//    - Performance summary, signatures and report footer.
//    - Existing report data structure preserved.
//    - Automatic PDF download and callbacks preserved.
//    ========================================================================== */

// const SCHOOL_NAME = "Zion Praise Academy CRM School System";

// const SCHOOL_SUBTITLE =
//   "Elementary, Junior & Senior High School";

// const SCHOOL_ADDRESS =
//   "Cowfield 1st Junction Duportroad Paynesville, Monrovia, Liberia";

// const SCHOOL_CONTACT =
//   "Contact: +231-775-912-321 / +231-881-403-326 / +231-777-506-311";

// const SYSTEM_NAME = "Polu Integrated School Management System";

// /* ==========================================================================
//    COLOR PALETTE
//    ========================================================================== */

// const COLORS = {
//   royalBlue: "#000000",
//   royalDeep: "#0A2B61",
//   royalMid: "#CD9E04",
//   skyBlue: "#65B9F2",
//   skyLight: "#EAF6FF",
//   paleBlue: "#F4F9FE",
//   softBlue: "#E0EFFB",
//   white: "#FFFFFF",
//   page: "#FFFFFF",
//   text: "#202A36",
//   textSecondary: "#4B5563",
//   muted: "#6B7280",
//   border: "#D9E4EF",
//   borderDark: "#B8CADC",
//   blue: "#013CAC",
//   greenLight: "#EDF8F1",
//   red: "#B42332",
//   redLight: "#FCEFF0",
//   grayLight: "#F7F9FC",
//   gray: "#E9EEF4",
// };

// /* ==========================================================================
//    PAGE CONFIGURATION
//    ========================================================================== */

// const API_BASE = api?.defaults?.baseURL || "";

// const PAGE_PADDING_X = 14;
// const PAGE_PADDING_TOP = 9;
// const PAGE_PADDING_BOTTOM = 25;

// const CARD_HEIGHT = 548;

// /* ==========================================================================
//    PROFESSIONAL PDF STYLES
//    ========================================================================== */

// const styles = StyleSheet.create({
//   /* PAGE */

//   page: {
//     paddingTop: PAGE_PADDING_TOP,
//     paddingBottom: PAGE_PADDING_BOTTOM,
//     paddingHorizontal: PAGE_PADDING_X,
//     backgroundColor: COLORS.page,
//     fontFamily: "Helvetica",
//     color: COLORS.text,
//   },

//   reportWrapper: {
//     width: "100%",
//     height: CARD_HEIGHT,
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "stretch",
//   },

//   /* STUDENT CARD */

//   card: {
//     width: "49.45%",
//     height: CARD_HEIGHT,
//     paddingTop: 9,
//     paddingBottom: 6,
//     paddingHorizontal: 9,
//     backgroundColor: COLORS.white,
//     borderWidth: 0.8,
//     borderColor: COLORS.borderDark,
//     borderRadius: 5,
//     position: "relative",
//     overflow: "hidden",
//   },

//   emptyCard: {
//     width: "49.45%",
//     height: CARD_HEIGHT,
//   },

//   cardTopAccent: {
//     position: "absolute",
//     top: 0,
//     left: 0,
//     right: 0,
//     height: 3,
//     backgroundColor: COLORS.royalDeep,
//   },

//   cardSkyAccent: {
//     position: "absolute",
//     top: 3,
//     left: 0,
//     width: "32%",
//     height: 2,
//     backgroundColor: COLORS.skyBlue,
//   },

//   /* SCHOOL HEADER */

//   header: {
//     flexDirection: "row",
//     alignItems: "center",
//     paddingTop: 2,
//     paddingBottom: 5,
//     marginBottom: 4,
//     borderBottomWidth: 0.8,
//     borderBottomColor: COLORS.border,
//   },

//   logoFrame: {
//     width: 43,
//     height: 43,
//     flexShrink: 0,
//     marginRight: 8,
//     padding: 2,
//     borderWidth: 0.8,
//     borderColor: COLORS.softBlue,
//     borderRadius: 5,
//     backgroundColor: COLORS.white,
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   logo: {
//     width: 37,
//     height: 37,
//     objectFit: "contain",
//   },

//   schoolInfo: {
//     flexGrow: 1,
//     flexShrink: 1,
//     flexBasis: 0,
//     minWidth: 0,
//     paddingRight: 5,
//   },

//   schoolName: {
//     fontSize: 12,
//     fontWeight: "bold",
//     color: COLORS.royalDeep,
//     marginBottom: 2,
//   },

//   schoolSubtitle: {
//     fontSize: 7,
//     fontWeight: "bold",
//     color: COLORS.royalMid,
//     marginBottom: 2,
//   },

//   schoolAddress: {
//     fontSize: 6.2,
//     lineHeight: 1.25,
//     color: COLORS.textSecondary,
//     marginBottom: 1,
//   },

//   schoolReportBadge: {
//     alignSelf: "flex-start",
//     backgroundColor: COLORS.skyLight,
//     borderWidth: 0.6,
//     borderColor: COLORS.softBlue,
//     borderRadius: 3,
//     paddingVertical: 3,
//     paddingHorizontal: 6,
//     marginTop: 3,
//   },

//   reportTitle: {
//     fontSize: 7,
//     fontWeight: "bold",
//     color: COLORS.royalDeep,
//     letterSpacing: 0.4,
//   },

//   /* STUDENT PHOTO */

//   admissionPhotoColumn: {
//     width: 48,
//     flexShrink: 0,
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   admissionPhotoFrame: {
//     width: 42,
//     height: 47,
//     padding: 2,
//     borderWidth: 0.8,
//     borderColor: COLORS.skyBlue,
//     borderRadius: 4,
//     backgroundColor: COLORS.white,
//     alignItems: "center",
//     justifyContent: "center",
//     overflow: "hidden",
//   },

//   admissionPhoto: {
//     width: 36,
//     height: 41,
//     objectFit: "cover",
//     borderRadius: 2,
//   },

//   photoPlaceholder: {
//     width: 36,
//     height: 41,
//     backgroundColor: COLORS.skyLight,
//     alignItems: "center",
//     justifyContent: "center",
//     borderRadius: 2,
//   },

//   photoPlaceholderText: {
//     fontSize: 5.5,
//     fontWeight: "bold",
//     textAlign: "center",
//     color: COLORS.royalDeep,
//   },

//   photoCaption: {
//     marginTop: 2,
//     fontSize: 5.5,
//     color: COLORS.muted,
//     textAlign: "center",
//   },

//   /* SECTION HEADINGS */

//   sectionHeading: {
//     flexDirection: "row",
//     alignItems: "center",
//     marginTop: 3,
//     marginBottom: 4,
//   },

//   sectionAccent: {
//     width: 3,
//     height: 10,
//     backgroundColor: COLORS.skyBlue,
//     borderRadius: 2,
//     marginRight: 5,
//   },

//   sectionHeadingText: {
//     fontSize: 8,
//     color: COLORS.royalDeep,
//     fontWeight: "bold",
//     letterSpacing: 0.4,
//   },

//   /* STUDENT INFORMATION */

//   infoBox: {
//     flexDirection: "row",
//     alignItems: "stretch",
//     backgroundColor: COLORS.paleBlue,
//     borderWidth: 0.7,
//     borderColor: COLORS.border,
//     borderRadius: 4,
//     paddingVertical: 5,
//     paddingHorizontal: 6,
//     marginBottom: 4,
//   },

//   studentDetails: {
//     flexGrow: 1,
//     flexShrink: 1,
//     flexBasis: 0,
//     paddingRight: 5,
//     justifyContent: "center",
//   },

//   infoRow: {
//     flexDirection: "row",
//     alignItems: "center",
//     marginBottom: 3,
//   },

//   infoRowLast: {
//     marginBottom: 0,
//   },

//   label: {
//     width: 75,
//     flexShrink: 0,
//     fontSize: 7,
//     color: COLORS.textSecondary,
//     fontWeight: "bold",
//   },

//   value: {
//     flexGrow: 1,
//     flexShrink: 1,
//     fontSize: 7.5,
//     color: COLORS.text,
//     fontWeight: "bold",
//   },

//   studentNameValue: {
//     color: COLORS.royalDeep,
//     fontSize: 8.5,
//   },

//   /* QR CODE */

//   qrColumn: {
//     width: 48,
//     flexShrink: 0,
//     alignItems: "center",
//     justifyContent: "center",
//     borderLeftWidth: 0.7,
//     borderLeftColor: COLORS.border,
//     paddingLeft: 5,
//   },

//   qrFrame: {
//     width: 35,
//     height: 35,
//     padding: 2,
//     borderWidth: 0.7,
//     borderColor: COLORS.softBlue,
//     borderRadius: 3,
//     backgroundColor: COLORS.white,
//     alignItems: "center",
//     justifyContent: "center",
//   },

//   qrImage: {
//     width: 29,
//     height: 29,
//     objectFit: "contain",
//   },

//   qrCaption: {
//     marginTop: 2,
//     fontSize: 5,
//     fontWeight: "bold",
//     color: COLORS.royalDeep,
//     textAlign: "center",
//   },

//   /* SUBJECT TABLE */

//   table: {
//     width: "100%",
//     borderWidth: 0.8,
//     borderColor: COLORS.borderDark,
//     borderRadius: 3,
//     overflow: "hidden",
//     marginTop: 1,
//   },

//   tableHeader: {
//     flexDirection: "row",
//     alignItems: "stretch",
//     backgroundColor: COLORS.royalDeep,
//     borderBottomWidth: 1.3,
//     borderBottomColor: COLORS.skyBlue,
//   },

//   headerSubject: {
//     width: "74%",
//     paddingVertical: 5,
//     paddingLeft: 7,
//     paddingRight: 4,
//     fontSize: 8,
//     fontWeight: "bold",
//     color: COLORS.white,
//     letterSpacing: 0.4,
//     borderRightWidth: 0.8,
//     borderRightColor: COLORS.borderDark,
//   },

//   headerScore: {
//     width: "26%",
//     paddingVertical: 5,
//     paddingHorizontal: 3,
//     textAlign: "center",
//     fontSize: 8,
//     fontWeight: "bold",
//     color: COLORS.white,
//     letterSpacing: 0.35,
//   },

//   /*
//    * Each row centers both columns.
//    * The score circle remains smaller than the available row height.
//    */

//   tableRow: {
//     width: "100%",
//     flexDirection: "row",
//     alignItems: "center",
//     borderBottomWidth: 0.55,
//     borderBottomColor: COLORS.borderDark,
//   },

//   tableRowAlternate: {
//     backgroundColor: COLORS.paleBlue,
//   },

//   /* SUBJECT CELL */

//   subjectCell: {
//     width: "74%",
//     height: "100%",
//     paddingLeft: 7,
//     paddingRight: 5,
//     paddingVertical: 1.5,
//     borderRightWidth: 0.7,
//     borderRightColor: COLORS.borderDark,
//     justifyContent: "center",
//     alignItems: "flex-start",
//   },

//   subjectText: {
//     width: "100%",
//     fontSize: 8,
//     color: COLORS.text,
//     textAlign: "left",
//     lineHeight: 1.15,
//   },

//   /* SCORE CELL */

//   scoreCell: {
//     width: "26%",
//     height: "100%",
//     flexDirection: "row",
//     alignItems: "center",
//     justifyContent: "center",
//     paddingHorizontal: 3,
//     paddingVertical: 1.5,
//   },

//   /* FIXED CIRCULAR SCORE BORDER */

//   scorePill: {
//     width: 16,
//     height: 16,
//     flexShrink: 0,
//     borderWidth: 0.7,
//     borderColor: COLORS.borderDark,
//     borderRadius: 8,
//     backgroundColor: COLORS.white,
//     alignItems: "center",
//     justifyContent: "center",
//     alignSelf: "center",
//   },

//   scorePillGood: {
//     borderColor: "#B7DCC5",
//     backgroundColor: COLORS.greenLight,
//   },

//   scorePillBad: {
//     borderColor: "#E8B9BE",
//     backgroundColor: COLORS.redLight,
//   },

//   scorePillNeutral: {
//     borderColor: COLORS.softBlue,
//     backgroundColor: COLORS.white,
//   },

//   scoreText: {
//     fontSize: 7.2,
//     fontWeight: "bold",
//     textAlign: "center",
//     color: COLORS.royalDeep,
//   },

//   scoreGood: {
//     color: COLORS.blue,
//   },

//   scoreBad: {
//     color: COLORS.red,
//   },

//   scoreNeutral: {
//     color: COLORS.royalDeep,
//   },

//   emptySubjects: {
//     width: "100%",
//     textAlign: "center",
//     fontSize: 7.5,
//     paddingVertical: 8,
//     color: COLORS.textSecondary,
//   },

//   /* PERFORMANCE SUMMARY */

//   summaryBox: {
//     marginTop: 5,
//     paddingVertical: 5,
//     paddingHorizontal: 7,
//     backgroundColor: COLORS.paleBlue,
//     borderWidth: 0.7,
//     borderColor: COLORS.border,
//     borderLeftWidth: 2,
//     borderLeftColor: COLORS.royalDeep,
//     borderRadius: 3,
//   },

//   summaryHeading: {
//     fontSize: 7.5,
//     fontWeight: "bold",
//     color: COLORS.royalDeep,
//     marginBottom: 4,
//     letterSpacing: 0.3,
//   },

//   summaryRow: {
//     flexDirection: "row",
//     alignItems: "center",
//     justifyContent: "space-between",
//     marginBottom: 3,
//   },

//   summaryRowLast: {
//     marginBottom: 0,
//   },

//   summaryLabel: {
//     flex: 1,
//     fontSize: 7,
//     color: COLORS.textSecondary,
//     fontWeight: "bold",
//   },

//   summaryValue: {
//     flex: 1,
//     fontSize: 7.5,
//     color: COLORS.text,
//     fontWeight: "bold",
//     textAlign: "right",
//   },

//   averagePill: {
//     backgroundColor: COLORS.white,
//     borderWidth: 0.7,
//     borderColor: COLORS.softBlue,
//     borderRadius: 12,
//     paddingVertical: 2,
//     paddingHorizontal: 6,
//     alignSelf: "flex-end",
//   },

//   averageValue: {
//     fontSize: 8,
//     color: COLORS.royalDeep,
//     fontWeight: "bold",
//   },

//   remarkValue: {
//     color: COLORS.blue,
//   },

//   /* SIGNATURES */

//   signatures: {
//     marginTop: 7,
//     flexDirection: "row",
//     justifyContent: "space-between",
//   },

//   signatureBox: {
//     width: "43%",
//     alignItems: "center",
//   },

//   signatureLine: {
//     width: "100%",
//     borderTopWidth: 0.8,
//     borderTopColor: COLORS.borderDark,
//     marginBottom: 3,
//   },

//   signatureText: {
//     fontSize: 7.2,
//     fontWeight: "bold",
//     color: COLORS.royalDeep,
//     textAlign: "center",
//   },

//   signatureSubtext: {
//     fontSize: 6.2,
//     color: COLORS.muted,
//     textAlign: "center",
//     marginTop: 1,
//   },

//   /* CARD FOOTER */

//   cardFooter: {
//     marginTop: 5,
//     paddingTop: 3,
//     borderTopWidth: 0.6,
//     borderTopColor: COLORS.border,
//     alignItems: "center",
//   },

//   cardFooterText: {
//     fontSize: 5.3,
//     color: COLORS.muted,
//     textAlign: "center",
//   },

//   /* PAGE FOOTER */

//   pageFooter: {
//     position: "absolute",
//     left: PAGE_PADDING_X,
//     right: PAGE_PADDING_X,
//     bottom: 8,
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//     borderTopWidth: 0.8,
//     borderTopColor: COLORS.border,
//     paddingTop: 3,
//   },

//   pageFooterLeft: {
//     fontSize: 6,
//     color: COLORS.textSecondary,
//   },

//   pageFooterRight: {
//     fontSize: 6.5,
//     color: COLORS.royalDeep,
//     fontWeight: "bold",
//   },
// });

// /* ==========================================================================
//    SCORE HELPERS
//    ========================================================================== */

// const isLowScore = (score) => {
//   if (typeof score === "number") {
//     return score < 70;
//   }

//   if (typeof score === "string") {
//     const normalized = score.trim().toUpperCase();

//     if (["D", "F"].includes(normalized)) {
//       return true;
//     }

//     const numericScore = Number(normalized);

//     if (normalized !== "" && Number.isFinite(numericScore)) {
//       return numericScore < 70;
//     }
//   }

//   return false;
// };

// const getScoreStyle = (score) => {
//   if (score === null || score === undefined || score === "") {
//     return styles.scoreNeutral;
//   }

//   return isLowScore(score)
//     ? styles.scoreBad
//     : styles.scoreGood;
// };

// const getScorePillStyle = (score) => {
//   if (score === null || score === undefined || score === "") {
//     return styles.scorePillNeutral;
//   }

//   return isLowScore(score)
//     ? styles.scorePillBad
//     : styles.scorePillGood;
// };

// const groupReports = (reports = []) => {
//   const pages = [];

//   for (let i = 0; i < reports.length; i += 2) {
//     pages.push(reports.slice(i, i + 2));
//   }

//   return pages;
// };

// const displayValue = (value, fallback = "N/A") => {
//   if (value === null || value === undefined || value === "") {
//     return fallback;
//   }

//   return String(value);
// };

// /* ==========================================================================
//    ADMISSION PHOTO URL HELPERS
//    ========================================================================== */

// const getImageString = (value) => {
//   if (typeof value === "string") {
//     const result = value.trim();
//     return result || null;
//   }

//   if (value && typeof value === "object") {
//     if (typeof value.url === "string" && value.url.trim()) {
//       return value.url.trim();
//     }

//     if (typeof value.uri === "string" && value.uri.trim()) {
//       return value.uri.trim();
//     }
//   }

//   return null;
// };

// const makeAbsoluteMediaUrl = (imageUrl) => {
//   if (!imageUrl || typeof imageUrl !== "string") {
//     return null;
//   }

//   const value = imageUrl.trim();

//   if (!value) {
//     return null;
//   }

//   if (/^https?:\/\//i.test(value)) {
//     return value;
//   }

//   if (/^data:image\//i.test(value)) {
//     return value;
//   }

//   if (value.startsWith("//")) {
//     if (typeof window !== "undefined" && window.location?.protocol) {
//       return `${window.location.protocol}${value}`;
//     }

//     return `https:${value}`;
//   }

//   if (API_BASE) {
//     try {
//       const apiUrl =
//         typeof window !== "undefined"
//           ? new URL(API_BASE, window.location.origin)
//           : new URL(API_BASE);

//       if (value.startsWith("/")) {
//         return `${apiUrl.origin}${value}`;
//       }

//       return `${apiUrl.origin}/${value}`;
//     } catch (error) {
//       console.warn(
//         "Could not resolve API base URL for student photo."
//       );
//     }
//   }

//   if (typeof window !== "undefined" && window.location?.origin) {
//     try {
//       return new URL(value, window.location.origin).href;
//     } catch (error) {
//       console.warn(
//         "Could not resolve student admission photo URL."
//       );
//     }
//   }

//   return value;
// };

// const getAdmissionImage = (report) => {
//   const candidates = [
//     report?.student_admission_image_url,
//     report?.student_admission_image,
//     report?.admission_image_url,
//     report?.admission_image,

//     report?.student?.student_admission_image_url,
//     report?.student?.student_admission_image,
//     report?.student?.admission_image_url,
//     report?.student?.admission_image,

//     report?.student_admission?.student_admission_image_url,
//     report?.student_admission?.student_admission_image,
//     report?.student_admission?.admission_image_url,
//     report?.student_admission?.admission_image,

//     report?.admission?.student_admission_image_url,
//     report?.admission?.student_admission_image,
//     report?.admission?.admission_image_url,
//     report?.admission?.admission_image,

//     report?.student?.admission?.student_admission_image_url,
//     report?.student?.admission?.student_admission_image,
//   ];

//   for (const candidate of candidates) {
//     const image = getImageString(candidate);

//     if (image) {
//       return makeAbsoluteMediaUrl(image);
//     }
//   }

//   return null;
// };

// /* ==========================================================================
//    STUDENT PERIODIC REPORT CARD
//    ========================================================================== */

// const StudentGradeCard = ({ report }) => {
//   if (!report) {
//     return <View style={styles.emptyCard} />;
//   }

//   const subjects = Array.isArray(report?.student_subjects)
//     ? report.student_subjects
//     : [];

//   const subjectCount = subjects.length;

//   /*
//    * Keep rows taller than their score circles.
//    * A modest minimum row height protects the text and circle spacing.
//    */

//   const subjectRowHeight =
//     subjectCount === 0
//       ? 14
//       : subjectCount > 32
//         ? 12
//         : subjectCount > 24
//           ? 14
//           : subjectCount > 16
//             ? 16
//             : 19;

//   const subjectFontSize =
//     subjectCount > 32
//       ? 5.8
//       : subjectCount > 24
//         ? 6.4
//         : subjectCount > 16
//           ? 7
//           : 8;

//   const scoreFontSize =
//     subjectCount > 32
//       ? 4.8
//       : subjectCount > 24
//         ? 5.4
//         : subjectCount > 16
//           ? 6
//           : 7.2;

//   const scoreCircleSize =
//     subjectCount > 32
//       ? 9
//       : subjectCount > 24
//         ? 11
//         : subjectCount > 16
//           ? 13
//           : 16;

//   const admissionImage = getAdmissionImage(report);

//   const studentName =
//     report.student_name ||
//     report.full_name ||
//     report.student?.full_name ||
//     report.student?.student_name;

//   const gradeClassName =
//     report.grade_class_name ||
//     report.grade_class?.name;

//   const periodName =
//     report.period_name ||
//     report.period?.name;

//   const academicYearName =
//     report.academic_year?.name ||
//     report.academic_year_name ||
//     (typeof report.academic_year === "string"
//       ? report.academic_year
//       : undefined);

//   return (
//     <View style={styles.card} wrap={false}>
//       <View style={styles.cardTopAccent} />
//       <View style={styles.cardSkyAccent} />

//       {/* SCHOOL HEADER */}

//       <View style={styles.header}>
//         <View style={styles.logoFrame}>
//           <Image src={logo} style={styles.logo} />
//         </View>

//         <View style={styles.schoolInfo}>
//           <Text style={styles.schoolName}>
//             {SCHOOL_NAME}
//           </Text>

//           <Text style={styles.schoolSubtitle}>
//             {SCHOOL_SUBTITLE}
//           </Text>

//           <Text style={styles.schoolAddress}>
//             {SCHOOL_ADDRESS}
//           </Text>

//           <Text style={styles.schoolAddress}>
//             {SCHOOL_CONTACT}
//           </Text>

//           <View style={styles.schoolReportBadge}>
//             <Text style={styles.reportTitle}>
//               STUDENT PERIODIC REPORT
//             </Text>
//           </View>
//         </View>

//         <View style={styles.admissionPhotoColumn}>
//           <View style={styles.admissionPhotoFrame}>
//             {admissionImage ? (
//               <Image
//                 src={admissionImage}
//                 style={styles.admissionPhoto}
//               />
//             ) : (
//               <View style={styles.photoPlaceholder}>
//                 <Text style={styles.photoPlaceholderText}>
//                   {"STUDENT\nPHOTO"}
//                 </Text>
//               </View>
//             )}
//           </View>

//           <Text style={styles.photoCaption}>
//             STUDENT PHOTO
//           </Text>
//         </View>
//       </View>

//       {/* STUDENT INFORMATION */}

//       <View style={styles.sectionHeading}>
//         <View style={styles.sectionAccent} />
//         <Text style={styles.sectionHeadingText}>
//           STUDENT INFORMATION
//         </Text>
//       </View>

//       <View style={styles.infoBox}>
//         <View style={styles.studentDetails}>
//           <View style={styles.infoRow}>
//             <Text style={styles.label}>Student Name</Text>
//             <Text
//               style={[styles.value, styles.studentNameValue]}
//               maxLines={1}
//             >
//               {displayValue(studentName)}
//             </Text>
//           </View>

//           <View style={styles.infoRow}>
//             <Text style={styles.label}>Grade / Class</Text>
//             <Text style={styles.value} maxLines={1}>
//               {displayValue(gradeClassName)}
//             </Text>
//           </View>

//           <View style={styles.infoRow}>
//             <Text style={styles.label}>Academic Period</Text>
//             <Text style={styles.value} maxLines={1}>
//               {displayValue(periodName)}
//             </Text>
//           </View>

//           <View style={[styles.infoRow, styles.infoRowLast]}>
//             <Text style={styles.label}>Academic Year</Text>
//             <Text style={styles.value} maxLines={1}>
//               {displayValue(academicYearName)}
//             </Text>
//           </View>
//         </View>

//         <View style={styles.qrColumn}>
//           <View style={styles.qrFrame}>
//             <Image src={schoolQRCode} style={styles.qrImage} />
//           </View>

//           <Text style={styles.qrCaption}>
//             SCHOOL QR CODE
//           </Text>
//         </View>
//       </View>

//       {/* ACADEMIC PERFORMANCE */}

//       <View style={styles.sectionHeading}>
//         <View style={styles.sectionAccent} />
//         <Text style={styles.sectionHeadingText}>
//           ACADEMIC PERFORMANCE
//         </Text>
//       </View>

//       {/* SUBJECT AND SCORE TABLE */}

//       <View style={styles.table} wrap={false}>
//         <View style={styles.tableHeader} wrap={false}>
//           <Text style={styles.headerSubject}>
//             SUBJECTS
//           </Text>

//           <Text style={styles.headerScore}>
//             SCORE
//           </Text>
//         </View>

//         {subjects.length > 0 ? (
//           subjects.map((subject, index) => {
//             const score = subject.formatted_final_score;

//             return (
//               <View
//                 key={`${subject.subject_name || "subject"}-${index}`}
//                 style={[
//                   styles.tableRow,
//                   index % 2 === 1 && styles.tableRowAlternate,
//                   {
//                     height: subjectRowHeight,
//                   },
//                 ]}
//                 wrap={false}
//               >
//                 {/* CENTERED SUBJECT NAME */}

//                 <View style={styles.subjectCell}>
//                   <Text
//                     style={[
//                       styles.subjectText,
//                       {
//                         fontSize: subjectFontSize,
//                       },
//                     ]}
//                     maxLines={1}
//                   >
//                     {displayValue(
//                       subject.subject_name,
//                       "Unnamed Subject"
//                     )}
//                   </Text>
//                 </View>

//                 {/* CENTERED SCORE CIRCLE */}

//                 <View style={styles.scoreCell}>
//                   <View
//                     style={[
//                       styles.scorePill,
//                       getScorePillStyle(score),
//                       {
//                         width: scoreCircleSize,
//                         height: scoreCircleSize,
//                         borderRadius: scoreCircleSize / 2,
//                       },
//                     ]}
//                   >
//                     <Text
//                       style={[
//                         styles.scoreText,
//                         getScoreStyle(score),
//                         {
//                           fontSize: scoreFontSize,
//                         },
//                       ]}
//                       maxLines={1}
//                     >
//                       {displayValue(score)}
//                     </Text>
//                   </View>
//                 </View>
//               </View>
//             );
//           })
//         ) : (
//           <View style={styles.tableRow} wrap={false}>
//             <Text style={styles.emptySubjects}>
//               No subject records are available for this report.
//             </Text>
//           </View>
//         )}
//       </View>

//       {/* PERFORMANCE SUMMARY */}

//       <View style={styles.summaryBox} wrap={false}>
//         <Text style={styles.summaryHeading}>
//           PERFORMANCE SUMMARY
//         </Text>

//         <View style={styles.summaryRow}>
//           <Text style={styles.summaryLabel}>
//             Average Score
//           </Text>

//           <View style={styles.averagePill}>
//             <Text
//               style={[
//                 styles.averageValue,
//                 getScoreStyle(report.average_score),
//               ]}
//             >
//               {displayValue(report.average_score)}
//             </Text>
//           </View>
//         </View>

//         <View style={styles.summaryRow}>
//           <Text style={styles.summaryLabel}>
//             Teacher's Remark
//           </Text>

//           <Text
//             style={[styles.summaryValue, styles.remarkValue]}
//             maxLines={2}
//           >
//             {displayValue(report.remark)}
//           </Text>
//         </View>

//         <View style={[styles.summaryRow, styles.summaryRowLast]}>
//           <Text style={styles.summaryLabel}>
//             Class Rank
//           </Text>

//           <Text style={styles.summaryValue}>
//             {displayValue(report.rank)}
//           </Text>
//         </View>
//       </View>

//       {/* SIGNATURES */}

//       <View style={styles.signatures} wrap={false}>
//         <View style={styles.signatureBox}>
//           <View style={styles.signatureLine} />

//           <Text style={styles.signatureText}>
//             Class Teacher
//           </Text>

//           <Text style={styles.signatureSubtext}>
//             Signature / Date
//           </Text>
//         </View>

//         <View style={styles.signatureBox}>
//           <View style={styles.signatureLine} />

//           <Text style={styles.signatureText}>
//             Principal
//           </Text>

//           <Text style={styles.signatureSubtext}>
//             Signature / Date
//           </Text>
//         </View>
//       </View>

//       {/* CARD FOOTER */}

//       <View style={styles.cardFooter}>
//         <Text style={styles.cardFooterText}>
//           {SCHOOL_NAME} • Generated by {SYSTEM_NAME}
//         </Text>
//       </View>
//     </View>
//   );
// };

// /* ==========================================================================
//    CLASS REPORT DOCUMENT
//    TWO STUDENTS PER A4 LANDSCAPE PAGE
//    ========================================================================== */

// export const ClassReportDocument = ({
//   reports = [],
//   gradeClass,
//   period,
// }) => {
//   const pages = groupReports(reports);

//   return (
//     <Document
//       title={`${SCHOOL_NAME} - ${gradeClass?.name || ""} ${
//         period?.name || ""
//       } Report`}
//       author={SCHOOL_NAME}
//       subject="Student Periodic Grade Report"
//       creator={SYSTEM_NAME}
//       producer="React PDF"
//     >
//       {pages.map((pageReports, pageIndex) => (
//         <Page
//           key={pageIndex}
//           size="A4"
//           orientation="landscape"
//           style={styles.page}
//           wrap={false}
//         >
//           <View style={styles.reportWrapper} wrap={false}>
//             <StudentGradeCard report={pageReports[0]} />

//             {pageReports[1] ? (
//               <StudentGradeCard report={pageReports[1]} />
//             ) : (
//               <View style={styles.emptyCard} />
//             )}
//           </View>

//           <View fixed style={styles.pageFooter}>
//             <Text style={styles.pageFooterLeft}>
//               {SCHOOL_NAME} • {SYSTEM_NAME}
//             </Text>

//             <Text
//               style={styles.pageFooterRight}
//               render={({ pageNumber, totalPages }) =>
//                 `PAGE ${pageNumber} OF ${totalPages}`
//               }
//             />
//           </View>
//         </Page>
//       ))}
//     </Document>
//   );
// };

// /* ==========================================================================
//    AUTOMATIC PDF DOWNLOAD
//    ========================================================================== */

// const ClassReportPDF = ({
//   reports,
//   gradeClass,
//   period,
//   onDownloaded,
//   onError,
// }) => {
//   const hasStartedRef = useRef(false);

//   useEffect(() => {
//     if (!reports?.length) {
//       hasStartedRef.current = false;
//       return undefined;
//     }

//     if (hasStartedRef.current) {
//       return undefined;
//     }

//     hasStartedRef.current = true;

//     let isCancelled = false;
//     let downloadUrl = null;

//     const generateAndDownload = async () => {
//       try {
//         const blob = await pdf(
//           <ClassReportDocument
//             reports={reports}
//             gradeClass={gradeClass}
//             period={period}
//           />
//         ).toBlob();

//         if (isCancelled) {
//           return;
//         }

//         downloadUrl = URL.createObjectURL(blob);

//         const link = document.createElement("a");

//         link.href = downloadUrl;

//         link.download = [
//           "Zion_Praise_Academy",
//           gradeClass?.name || "Class",
//           period?.name || "Report",
//         ]
//           .join("_")
//           .replace(/[<>:"/\\|?*\x00-\x1F]/g, "-") + ".pdf";

//         document.body.appendChild(link);
//         link.click();
//         document.body.removeChild(link);

//         onDownloaded?.();
//       } catch (error) {
//         if (!isCancelled) {
//           console.error("PDF generation failed:", error);
//           onError?.(error);
//         }
//       } finally {
//         if (downloadUrl) {
//           URL.revokeObjectURL(downloadUrl);
//         }
//       }
//     };

//     generateAndDownload();

//     return () => {
//       isCancelled = true;
//     };
//   }, [
//     reports,
//     gradeClass,
//     period,
//     onDownloaded,
//     onError,
//   ]);

//   return null;
// };

// export default ClassReportPDF;